import { ISimplifiedProjectView } from '../api-client/autogenerated';

/**
 * Project naming and ordering, kept in its own module so pure logic can use it without importing
 * `scripts/utils` - that module pulls in the API client, whose module graph is circular under
 * jest. `scripts/utils` re-exports these, so call sites can import from either.
 */

/**
 * A project's display name with its architect project number in front ("2301-Main St
 * Library"), or just the name when it has no number. Used wherever a project is picked or named
 * in navigation so the two read the same, and so typing the number finds the project.
 */
export const getProjectDisplayName = (project: {
  name: string;
  architectProjectNumber?: string | null;
}) => {
  const projectNumber = project.architectProjectNumber?.trim();
  return projectNumber ? `${projectNumber}-${project.name}` : project.name;
};

const alphanumericCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/**
 * Orders projects that have an architect project number ahead of those that do not, and the
 * numbered ones by that number alphanumerically ("2-..." before "10-..."). Returns 0 when neither
 * has a number or the numbers match, so the caller's own name ordering decides.
 */
export const compareArchitectProjectNumbers = (
  a: { architectProjectNumber?: string | null },
  b: { architectProjectNumber?: string | null },
) => {
  const number1 = a.architectProjectNumber?.trim();
  const number2 = b.architectProjectNumber?.trim();
  if (number1 && !number2) return -1;
  if (number2 && !number1) return 1;
  if (!number1 || !number2) return 0;
  return alphanumericCollator.compare(number1, number2);
};

/**
 * The project picker ordering: favorites first, archived projects last, and company-only access
 * below projects the user is on. Within each of those groups, numbered projects come first by
 * architect project number, then the rest by name -- matching the number shown in front of the
 * name.
 */
export const projectSort = (
  favoriteProjectIds: string[],
  a: ISimplifiedProjectView,
  b: ISimplifiedProjectView,
) => {
  const name1 = a.name.toLowerCase().trim();
  const name2 = b.name.toLowerCase().trim();
  const isFavorite1 = favoriteProjectIds.includes(a.id);
  const isFavorite2 = favoriteProjectIds.includes(b.id);
  if (isFavorite1 && !isFavorite2) return -1;
  if (isFavorite2 && !isFavorite1) return 1;
  // Archived projects are view-only history; they sink below everything still being worked on.
  if (!!a.isArchived !== !!b.isArchived) return a.isArchived ? 1 : -1;
  if (a.hasOnlyCompanyAccess !== b.hasOnlyCompanyAccess) return a.hasOnlyCompanyAccess ? 1 : -1;
  const byNumber = compareArchitectProjectNumbers(a, b);
  if (byNumber !== 0) return byNumber;
  if (name1 < name2) return -1;
  if (name1 > name2) return 1;
  return 0;
};
